import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import clsx from 'clsx';
import styles from './Button.module.scss';

export type NebiButtonVariant = 'primary' | 'ghost' | 'danger';
export type NebiButtonSize = 'sm' | 'md' | 'lg';

interface BaseProps {
  variant?: NebiButtonVariant;
  /** sm = 44px · md = 48px · lg = 54px (alturas do mockup) */
  size?: NebiButtonSize;
  /** Atenção: o `ui/Button` antigo tem default `true`. Aqui o default é `false`. */
  fullWidth?: boolean;
  loading?: boolean;
  children: ReactNode;
}

type ButtonAsButton = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & { as?: 'button' };
type ButtonAsAnchor = BaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & { as: 'a' };

export type NebiButtonProps = ButtonAsButton | ButtonAsAnchor;

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, NebiButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      children,
      className,
      ...rest
    },
    ref,
  ) {
    const cls = clsx(
      styles.button,
      styles[variant],
      styles[size],
      fullWidth && styles.fullWidth,
      loading && styles.loading,
      className,
    );
    const content = loading ? <span className={styles.spinner} aria-hidden="true" /> : children;

    if (rest.as === 'a') {
      const { as: _as, ...anchorProps } = rest as ButtonAsAnchor;
      return (
        <a ref={ref as React.Ref<HTMLAnchorElement>} className={cls} {...anchorProps}>
          {content}
        </a>
      );
    }

    const { as: _as, disabled, type, ...buttonProps } = rest as ButtonAsButton;
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type ?? 'button'}
        className={cls}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...buttonProps}
      >
        {content}
      </button>
    );
  },
);

export default Button;
